if (!customElements.get("m-new-arrivals-carousel")) {
  class MNewArrivalsCarousel extends HTMLElement {
    connectedCallback() {
      this.enableSlider = this.dataset.enableSlider === "true";
      this.showPagination = this.dataset.showPagination === "true";
      this.showNavigation = this.dataset.showNavigation === "true";
      this.slideCount = parseInt(this.dataset.slideCount, 10) || 0;
      this.autoplay = this.dataset.autoplay === "true";
      this.autoplayDelay = (parseFloat(this.dataset.autoplayDelay) || 5) * 1000;
      this.slides = Array.from(this.querySelectorAll(".m-na-carousel__slide"));

      // One category left standing is a static panel - the CSS already renders it
      // full width, so there is nothing to initialise beyond loading its cards.
      if (!this.enableSlider || this.slideCount < 2) {
        this.loadSlide(this.slides[0]);
        return;
      }

      this.container = this.querySelector(".m-na-carousel__container");
      if (!this.container || !window.MinimogLibs || !MinimogLibs.Swiper) return;

      this.controls = this.querySelector(".m-slider-controls");

      const start = () => {
        this.initSlider();
        if (this.autoplay) this.initAutoplay();
      };

      if (window.Shopify && Shopify.designMode) {
        start();
        // The editor can jump straight to any block, not just the ones Swiper
        // has physically reached yet - so load all of them up front here.
        this.slides.forEach((slide) => this.loadSlide(slide));
        return;
      }

      MinimogTheme.initWhenVisible({
        element: this,
        threshold: 400,
        callback: start,
      });
    }

    // Only the first visible slide ships with real product cards (see
    // new-arrivals-carousel.liquid) - every other slide is a tiny placeholder
    // until Swiper actually reaches it, then this fetches the real cards via
    // the Section Rendering API against that category's own collection URL.
    loadSlide(slide) {
      if (!slide || slide.dataset.loaded === "true") return;
      this.loadedPromises = this.loadedPromises || {};
      const index = slide.dataset.index;
      if (this.loadedPromises[index]) return this.loadedPromises[index];

      const slideId = slide.getAttribute("id");
      const productsContainer = slide.querySelector("[data-products-container]");

      this.loadedPromises[index] = fetchSection(this.dataset.sectionId, { url: slide.dataset.url, fromCache: true })
        .then((dom) => {
          const freshProducts = dom.querySelector(`#${slideId} [data-products-container]`);
          if (freshProducts && productsContainer) {
            productsContainer.innerHTML = freshProducts.innerHTML;
          }
          slide.dataset.loaded = "true";
          this.slider && this.slider.update();
        })
        .catch((err) => console.error(`Failed to load New Arrivals slide ${index}:`, err));

      return this.loadedPromises[index];
    }

    initSlider() {
      const prevButton = this.controls && this.controls.querySelector(".m-slider-controls__button-prev");
      const nextButton = this.controls && this.controls.querySelector(".m-slider-controls__button-next");

      this.slider = new MinimogLibs.Swiper(this.container, {
        // One whole category per slide, at every breakpoint. The 2/3/4 product
        // columns inside a slide are plain CSS grid, not a nested swiper.
        slidesPerView: 1,
        // Deliberately off. Cycling is handled by wrapping to slide 0 in the timer
        // below instead, because this build of Swiper predates `rewind` and its
        // `loop` implementation clones slides - which would duplicate every product
        // card and its image in the DOM for no visual gain across 3 slides.
        loop: false,
        autoHeight: true,
        threshold: 4,
        pagination: this.showPagination
          ? { el: this.querySelector(".swiper-pagination"), clickable: true }
          : false,
        on: {
          slideChange: (swiper) => this.loadSlide(this.slides[swiper.activeIndex]),
        },
      });

      if (this.showNavigation) {
        prevButton && prevButton.addEventListener("click", () => this.slider.slidePrev());
        nextButton && nextButton.addEventListener("click", () => this.slider.slideNext());
      }

      this.swiper = this.container.swiper;
      // Slide 0 is already loaded server-side (or matched the current page's
      // collection) - only fires network requests for slides 1+ once reached.
      this.loadSlide(this.slides[0]);
      this.prefetchOtherSlides();
    }

    // Fetching a slide only once Swiper reaches it means a shopper who
    // swipes/autoplays there sees the empty skeleton for a beat first. Filling
    // the rest in during idle time removes that flash at no cost to initial load.
    prefetchOtherSlides() {
      const schedule = window.requestIdleCallback || ((cb) => setTimeout(cb, 1500));
      schedule(() => {
        this.slides.forEach((slide) => this.loadSlide(slide));
      });
    }

    initAutoplay() {
      // Someone who has asked the OS for less motion should not get a carousel that
      // moves on its own.
      const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
      if (reduceMotion.matches) return;

      this.paused = false;
      this.inView = true;

      this.tick = () => {
        if (!this.slider || this.paused || document.hidden || !this.inView) return;
        if (this.slider.isEnd) this.slider.slideTo(0);
        else this.slider.slideNext();
      };
      this.timer = setInterval(this.tick, this.autoplayDelay);

      // Pause while the visitor is reading or interacting with the row.
      this.onEnter = () => (this.paused = true);
      this.onLeave = () => (this.paused = false);
      this.addEventListener("pointerenter", this.onEnter);
      this.addEventListener("pointerleave", this.onLeave);
      this.addEventListener("focusin", this.onEnter);
      this.addEventListener("focusout", this.onLeave);
      this.addEventListener("touchstart", this.onEnter, { passive: true });

      // Do not burn CPU animating a section nobody is looking at.
      if ("IntersectionObserver" in window) {
        this.observer = new IntersectionObserver(
          (entries) => entries.forEach((e) => (this.inView = e.isIntersecting)),
          { threshold: 0 }
        );
        this.observer.observe(this);
      }
    }

    stopAutoplay() {
      if (this.timer) clearInterval(this.timer);
      this.timer = null;
      if (this.observer) this.observer.disconnect();
      this.removeEventListener("pointerenter", this.onEnter);
      this.removeEventListener("pointerleave", this.onLeave);
      this.removeEventListener("focusin", this.onEnter);
      this.removeEventListener("focusout", this.onLeave);
      this.removeEventListener("touchstart", this.onEnter);
    }

    disconnectedCallback() {
      this.stopAutoplay();
      if (this.slider && typeof this.slider.destroy === "function") {
        this.slider.destroy(true, true);
      }
    }
  }

  customElements.define("m-new-arrivals-carousel", MNewArrivalsCarousel);
}
